<script lang="ts">
    import { getModalStore, getToastStore } from "@skeletonlabs/skeleton";
    import TrashCan from "$lib/assets/TrashCan.svg";
    import XMark from "$lib/assets/XMark.svg";
    import RedX from "$lib/assets/RedX.svg";
    import { onMount } from "svelte";

    const toastStore = getToastStore();
    const modalStore = getModalStore();

    let categories = {};
    let originalCategories = {};
    let categoryCount = 1;
    let selectedCategoryToDelete: any = null;

    // Create deep copy of categories to track diffs
    onMount(() => {
        categories = fetch("/api/user_documents/edit_categories", {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
        })
            .then((resp) => {
                return resp.json();
            })
            .then((res) => {
                categories = res.data;
                for (const [category, tags] of Object.entries(categories)) {
                    // key is category, value is array of tags
                    categories[category] = tags.map((tag) => tag[0]);
                }
                originalCategories = JSON.parse(JSON.stringify(categories));
            });
    });

    // Function to update category and propagate changes to the categories object
    function updateCategoryName(oldCategory: string, newCategory: string) {
        if (newCategory !== oldCategory && newCategory !== "") {
            // hasOwnProperty checks only for properties that belong to object directly (not inherited from prototype)
            if (categories.hasOwnProperty(newCategory)) {
                alert(`Category "${newCategory}" already exists. Please choose a different name.`);
                return;
            }

            categories[newCategory] = categories[oldCategory];
            delete categories[oldCategory];
        }
    }

    function addCategory() {
        const newCategoryName = `New Category ${categoryCount++}`;
        categories = { ...categories, [newCategoryName]: [""] };
    }

    function deleteCategory(category: string) {
        const { [category]: _, ...rest } = categories;
        categories = rest;
        selectedCategoryToDelete = null;
    }

    function updateTags(category: string, tags: string[]) {
        categories = { ...categories, [category]: tags };
    }

    // Determine if a category or tag was deleted by comparing current categories with originalCategories
    function findDeletedItems() {
        const deletedTags = [];

        for (const [category, originalTags] of Object.entries(originalCategories)) {
            if (!categories[category]) {
                deletedTags.push(...originalTags.map((tag) => ({ category, tag })));
            } else {
                const currentTags = categories[category];
                const removedTags = originalTags.filter((tag) => !currentTags.includes(tag));

                deletedTags.push(...removedTags.map((tag) => ({ category, tag })));
            }
        }

        return { deletedTags };
    }

    // Creates the new category and tags in DB with the provided parameters and returns a toast afterwards
    async function update_value(event: Event): Promise<void> {
        event.target!.disabled = true;

        const fetchPromises = [];
        const { deletedTags } = findDeletedItems();

        let allSuccess = true;

        // Upserts for existing or new categories / tags. Only make API call if there's a diff.
        for (const [category, tags] of Object.entries(categories)) {
            for (const tag of tags) {
                if (
                    originalCategories[category] &&
                    JSON.stringify(originalCategories[category]) == JSON.stringify(tags)
                ) {
                    continue;
                }
                const fetchPromise = await fetch("/api/user_documents/edit_categories", {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        category: category,
                        tag: tag,
                    }),
                })
                    .then((resp) => {
                        if (!resp.ok) {
                            allSuccess = false;
                        }
                        return resp;
                    })
                    .catch((error) => {
                        allSuccess = false;
                    });

                fetchPromises.push(fetchPromise);
            }
        }

        // Handle deletion for removed categories / tags
        for (const { category, tag } of deletedTags) {
            const fetchPromise = fetch("/api/user_documents/edit_categories", {
                method: "DELETE",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    category: category,
                    tag: tag,
                }),
            })
                .then((resp) => {
                    if (!resp.ok) {
                        allSuccess = false;
                    }
                    return resp;
                })
                .catch((error) => {
                    allSuccess = false;
                });

            fetchPromises.push(fetchPromise);
        }

        await Promise.all(fetchPromises);

        if (allSuccess) {
            toastStore.trigger({
                message: "✓ Updated categories and tags",
                background: "bg-zinc-800 text-green-300 text-center",
                hideDismiss: true,
                timeout: 4000,
            });

            modalStore.close();
        } else {
            toastStore.trigger({
                message: "⚠️ Some categories or tags failed to update!",
                background: "bg-zinc-800 text-red-300 text-center",
                hideDismiss: true,
                timeout: 8000,
            });
        }

        event.target!.disabled = false;
    }
</script>

<div class="p-8 bg-white rounded-lg flex flex-col w-[700px]">
    <div class="flex flex-cols justify-between">
        <h1 class="text-xl font-bold mb-6">Edit Categories & Tags</h1>
        <button class="cursur-pointer" on:click={() => modalStore.close()}>
            <img src={XMark} alt="Close" class="h-6 w-6" />
        </button>
    </div>

    <!-- Display all existing categories -->
    {#if categories}
        {#each Object.entries(categories) as [category, tags]}
            <div>
                <!-- Category -->
                <div class="flex flex-cols mt-6">
                    <p class="text-md font-light">Category Name</p>
                    <div class="ml-6 flex">
                        <input
                            type="text"
                            value={category}
                            class="text-sm input rounded-lg border border-[solid] border-stone-200 mb-2"
                            placeholder={category}
                            disabled={selectedCategoryToDelete}
                            on:blur={(e) => updateCategoryName(category, e.target.value.trim())}
                        />
                        <img
                            class="h-5 w-6 mt-2 ml-3 transition duration-300 cursor-pointer"
                            src={RedX}
                            alt="Delete Category"
                            on:click={() => {
                                selectedCategoryToDelete = category;
                            }}
                        />
                    </div>
                    {#if selectedCategoryToDelete === category}
                        <button
                            class="ml-4 text-sm px-2 border-2 rounded-md bg-red-600 text-white transition duration-300 hover:shadow-md hover:cursor-pointer"
                            on:click={() => deleteCategory(category)}>Delete Category</button
                        >
                        <button
                            class="ml-4 text-sm px-2 border-2 rounded-md bg-zinc-800 text-white transition duration-300 hover:shadow-md hover:cursor-pointer"
                            on:click={() => (selectedCategoryToDelete = null)}>Cancel</button
                        >
                    {/if}
                </div>

                <!-- Tags -->
                <div class="flex">
                    <p class="text-md font-light mr-6">Tags</p>
                    <ul>
                        {#each tags as tag, index}
                            <li class="flex ml-20">
                                <input
                                    type="text"
                                    bind:value={tags[index]}
                                    class="text-sm input rounded-lg border border-[solid] border-stone-200 mb-2"
                                    disabled={selectedCategoryToDelete}
                                    placeholder="New Tag"
                                />
                                <img
                                    class="h-5 w-6 mt-2 ml-3 transition duration-300 cursor-pointer"
                                    src={TrashCan}
                                    alt="Delete Tag"
                                    on:click={() => {
                                        if (!selectedCategoryToDelete) {
                                            tags.splice(index, 1);
                                            updateTags(category, [...tags]);
                                        }
                                    }}
                                />
                            </li>
                        {/each}

                        <button
                            class="bg-green-600 text-white text-sm hover:shadow-md px-4 py-2 ml-20 rounded-lg mt-3 disabled:bg-secondary-600 transition duration-300"
                            disabled={selectedCategoryToDelete}
                            on:click={() => {
                                updateTags(category, [...tags, ""]);
                            }}
                        >
                            Add Another Tag
                        </button>
                    </ul>
                </div>
            </div>
        {/each}
    {/if}

    <div class="flex flex-cols justify-end">
        <button
            class="bg-blue-600 text-white text-sm hover:shadow-md px-4 py-2 rounded-lg mt-3 mr-6 disabled:bg-secondary-600 transition duration-300"
            disabled={selectedCategoryToDelete}
            on:click={() => {
                addCategory();
            }}
        >
            Add New Category
        </button>

        <button
            class="bg-zinc-800 text-white text-sm hover:shadow-md px-4 py-2 rounded-lg mt-3 disabled:bg-secondary-600 transition duration-300"
            disabled={selectedCategoryToDelete}
            on:click={(e) => {
                update_value(e);
            }}
        >
            Save
        </button>
    </div>
</div>
